import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router';
import { toast } from 'react-toastify';
import { SEOHelmet } from 'components/SEO';
import Pagination from 'components/Pagination/Pagination';
import CardSizeSelector from 'components/CardSizeSelector/CardSizeSelector';
import {
  PENDING_INCOMING_OFFERS_BADGE_ARGS,
  PENDING_OUTGOING_OFFERS_BADGE_ARGS,
  useGetTradeOfferQuery,
  useListTradeOffersQuery,
} from 'store/services/tradeOffers/tradeOffersApi';
import { useGetTradeSuggestionsCountQuery } from 'store/services/tradeSuggestions/tradeSuggestionsApi';
import { useGetMeQuery } from 'store/services/user/meApi';
import { OfferStatusCounts, TradeOfferStatus } from 'types/interfaces/tradeOffer';
import { useCardSizePreference } from 'hooks/useCardSizePreference';
import { FEATURE_NAME } from 'utils/glossary';
import SuggestedTab from '../TradeSuggestions/SuggestedTab';
import OfferSummaryCard from './components/OfferSummaryCard';
import OffersTabs, { OffersTab } from './components/OffersTabs';
import OffersZeroState from './components/zeroState/OffersZeroState';
import { SuggestionPassProvider } from './context/SuggestionPassContext';
import useDeferredDecline from './hooks/useDeferredDecline';
import './TradeOffersPage.css';

const OFFERS_PAGE_SIZE = 20;
const HIGHLIGHT_MS = 2000;

const STATUS_FILTERS: Array<{ value: TradeOfferStatus | 'all'; label: string }> = [
  { value: 'all', label: 'All' },
  { value: 'pending', label: 'Pending' },
  { value: 'accepted', label: 'Accepted' },
  { value: 'declined', label: 'Declined' },
  { value: 'expired', label: 'Expired' },
  { value: 'cancelled', label: 'Cancelled' },
  { value: 'failed', label: 'Failed' },
];

/**
 * Read the tab from the URL. Anything unrecognised — and a missing param —
 * falls back to Received, which is the landing tab even when it is empty.
 */
const parseTab = (raw: string | null): OffersTab =>
  raw === 'suggested' || raw === 'sent' ? raw : 'received';

function TradeOffersPageInner() {
  const [statusFilter, setStatusFilter] = useState<TradeOfferStatus | 'all'>('all');
  const [page, setPage] = useState(1);
  const [cardSize, setCardSize] = useCardSizePreference('tradeOffers.cardSize');
  const deferredDecline = useDeferredDecline();

  const [searchParams, setSearchParams] = useSearchParams();

  // Derived DURING RENDER, never in an effect. This is what makes a cold load of
  // ?tab=suggested paint Suggested on the FIRST frame: an effect would paint
  // Received once before correcting itself, which is the flash the ticket bars.
  const tab = parseTab(searchParams.get('tab'));
  const isSuggested = tab === 'suggested';

  const setTab = useCallback(
    (next: OffersTab) => {
      setSearchParams(
        (prev) => {
          const params = new URLSearchParams(prev);
          // Received is the default, so it needs no param; the bare URL is its
          // canonical link. The other two are addressable.
          if (next === 'received') params.delete('tab');
          else params.set('tab', next);
          return params;
        },
        // replace, so working through the tabs does not build a back-button
        // trail the user has to click through to leave the page.
        { replace: true }
      );
    },
    [setSearchParams]
  );

  /** Drop ?offer= without disturbing ?tab=, which lives in the same query string. */
  const clearDeepLinkParam = useCallback(() => {
    setSearchParams(
      (prev) => {
        const params = new URLSearchParams(prev);
        params.delete('offer');
        return params;
      },
      { replace: true }
    );
  }, [setSearchParams]);

  // ?offer= deep link: the old review route redirects here; notification
  // emails and socket toasts land on the list with the target row highlighted.
  const deepLinkId = searchParams.get('offer');
  const [highlightId, setHighlightId] = useState<string | null>(null);
  const handledDeepLinkRef = useRef<string | null>(null);
  const { data: me } = useGetMeQuery(undefined, { skip: !deepLinkId });
  const {
    data: deepLinkData,
    isError: deepLinkError,
  } = useGetTradeOfferQuery(deepLinkId || '', { skip: !deepLinkId });

  useEffect(() => {
    setPage(1);
  }, [tab, statusFilter]);

  const { data, currentData, isLoading, isFetching } = useListTradeOffersQuery(
    {
      direction: tab === 'sent' ? 'outgoing' : 'incoming',
      status: statusFilter === 'all' ? undefined : statusFilter,
      page,
      limit: OFFERS_PAGE_SIZE,
    },
    // Suggested renders no offer list, so it must not hold a subscription to
    // one. Without this it would keep refetching the Received page underneath.
    { skip: isSuggested }
  );

  // Inline tab counts, all three PENDING-only. Received comes from the SAME
  // cache entry the sidebar badge subscribes to, so "the nav badge equals
  // received plus suggestions" is true by construction rather than by two
  // sources happening to agree. Sent uses the mirrored outgoing args.
  const { data: receivedBadge } = useListTradeOffersQuery(PENDING_INCOMING_OFFERS_BADGE_ARGS);
  const { data: sentBadge } = useListTradeOffersQuery(PENDING_OUTGOING_OFFERS_BADGE_ARGS);
  const { data: suggestionsCountData } = useGetTradeSuggestionsCountQuery();

  // Pill visibility (progressive disclosure). Counts are held PER DIRECTION so
  // a tab switch renders from that tab's last-known counts instead of briefly
  // showing the other tab's set or collapsing while the fetch is in flight.
  // Fed from currentData (arg-fresh), never data (which retains the previous
  // args' response across a direction switch). Two distinct fallbacks, per the
  // plan review: no response yet for this tab means render All plus the
  // selected pill only and EXPAND when data arrives (quieter than
  // contracting); a real response WITHOUT the field means an older backend —
  // render every pill for the rest of the session.
  //
  // This drives the STATUS PILLS only. It cannot drive the tab counts: it is
  // populated for the tab being viewed, so the other tab would show nothing.
  const [countsByTab, setCountsByTab] = useState<Partial<Record<OffersTab, OfferStatusCounts>>>({});
  const [countsUnsupported, setCountsUnsupported] = useState(false);
  useEffect(() => {
    if (!currentData) return;
    if (currentData.statusCounts) {
      const counts = currentData.statusCounts;
      setCountsByTab((prev) => ({ ...prev, [tab]: counts }));
    } else {
      setCountsUnsupported(true);
    }
  }, [currentData, tab]);

  const tabCounts = countsByTab[tab];
  // Visible = All (always) + the selected pill (sticky at zero, so declining
  // the last offer in a viewed filter never yanks the control away — it drops
  // off when the user selects something else) + any status with offers.
  const visibleFilters = countsUnsupported
    ? STATUS_FILTERS
    : STATUS_FILTERS.filter(
        (f) =>
          f.value === 'all' ||
          f.value === statusFilter ||
          (tabCounts ? tabCounts[f.value] > 0 : false)
      );

  // Server state wins over an optimistic local decline mid-undo-window
  useEffect(() => {
    if (data?.offers) deferredDecline.reconcile(data.offers);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  // Deep link, step 1: the single-offer fetch names the tab that owns the row
  useEffect(() => {
    if (!deepLinkId || !deepLinkData?.offer || !me) return;
    setTab(me.id === deepLinkData.offer.recipient.id ? 'received' : 'sent');
  }, [deepLinkId, deepLinkData, me, setTab]);

  // Deep link, step 2: once the list renders the row, scroll to it and
  // highlight briefly; then drop the param so refreshes don't re-run this.
  useEffect(() => {
    if (!deepLinkId || handledDeepLinkRef.current === deepLinkId) return;
    if (!data?.offers.some((o) => o.id === deepLinkId)) return;
    handledDeepLinkRef.current = deepLinkId;
    setHighlightId(deepLinkId);
    requestAnimationFrame(() => {
      document
        .getElementById(`offer-${deepLinkId}`)
        ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    });
    // No cleanup: list refetches re-run this effect and would cancel the timer
    // before it fires (the handled ref already prevents double-runs).
    setTimeout(() => {
      setHighlightId(null);
      clearDeepLinkParam();
    }, HIGHLIGHT_MS);
  }, [deepLinkId, data, clearDeepLinkParam]);

  // Deep link to an offer that no longer exists: quiet note, never an error page
  useEffect(() => {
    if (!deepLinkId || !deepLinkError) return;
    toast.info('That offer is no longer available.');
    clearDeepLinkParam();
  }, [deepLinkId, deepLinkError, clearDeepLinkParam]);

  const totalCount = data?.totalCount ?? 0;
  const totalPages = Math.max(1, Math.ceil(totalCount / OFFERS_PAGE_SIZE));
  useEffect(() => {
    if (isSuggested) return;
    if (data && page > 1 && page > totalPages) {
      setPage(totalPages);
    }
  }, [data, page, totalPages, isSuggested]);

  const isEmpty = !isLoading && !!data && data.offers.length === 0;
  // The full teaching zero state owns the unfiltered empty tab; a filtered
  // empty result keeps a small message so filters stay legible.
  const showZeroState = isEmpty && statusFilter === 'all';

  return (
    <>
      <SEOHelmet
        title={`${FEATURE_NAME} - TradeMagic`}
        description="See the trade offers you've received and sent, and accept or decline pending ones right from the list."
      />
      <div className="offers-page" aria-busy={isFetching}>
        <div className="offers-header-row">
          <h1 className="offers-heading">{FEATURE_NAME}</h1>
        </div>

        <OffersTabs
          activeTab={tab}
          onTabChange={setTab}
          counts={{
            received: receivedBadge?.totalCount,
            suggested: suggestionsCountData?.count,
            sent: sentBadge?.totalCount,
          }}
          // S/M/L belongs to the list tabs; the suggestion deck has no grid.
          trailing={
            isSuggested ? undefined : <CardSizeSelector value={cardSize} onChange={setCardSize} />
          }
        />

        {isSuggested ? (
          <SuggestedTab />
        ) : (
          <>
            <div className="offers-filters">
              {visibleFilters.map((f) => (
                <button
                  key={f.value}
                  type="button"
                  className={`offers-filter-pill${statusFilter === f.value ? ' active' : ''}`}
                  onClick={() => setStatusFilter(f.value)}
                >
                  {f.label}
                </button>
              ))}
            </div>

            <div className="offers-count">
              {data && !showZeroState
                ? `${totalCount.toLocaleString()} ${totalCount === 1 ? 'offer' : 'offers'}`
                : ''}
            </div>

            <div className={`offers-list size-${cardSize.toLowerCase()}`}>
              {isLoading ? (
                <div className="offers-status-msg">Loading offers…</div>
              ) : showZeroState ? (
                <OffersZeroState tab={tab === 'sent' ? 'sent' : 'received'} cardSize={cardSize} />
              ) : isEmpty ? (
                <div className="offers-status-msg">No offers match this filter.</div>
              ) : (
                data?.offers.map((offer) => (
                  <OfferSummaryCard
                    key={offer.id}
                    offer={offer}
                    tab={tab === 'sent' ? 'sent' : 'received'}
                    highlight={offer.id === highlightId}
                    deferredDecline={deferredDecline}
                  />
                ))
              )}
            </div>

            {data && totalPages > 1 && (
              <Pagination currentPage={page} totalPages={totalPages} onPageChange={setPage} />
            )}
          </>
        )}
      </div>
    </>
  );
}

/**
 * The pass provider wraps the whole page, ABOVE the tab switch, so a tab click
 * unmounts the suggestion deck without committing a pending pass. Leaving
 * /trade-offers unmounts this too, which still commits — that part is unchanged.
 */
export default function TradeOffersPage() {
  return (
    <SuggestionPassProvider>
      <TradeOffersPageInner />
    </SuggestionPassProvider>
  );
}
